
import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Grid } from "@mui/material";
import { motion } from "framer-motion";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const learningBenefits = [
  "One-on-One Guidance",
  "Flexible Learning",
  "Concept-Based Teaching",
  "Regular Practice",
  "Doubt Clarification",
  "Better Academic Performance",
];

const howItWorks = [
  "Explore Subjects",
  "Find the Right Tutor",
  "Connect & Learn",
  "Achieve Your Goals",
];

// Motion variants
const headingVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const cardLeftVariants = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const cardRightVariants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const listContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionList = motion(List);
const MotionListItem = motion(ListItem);

export default function LearningSection() {
  return (
    <Box sx={{ pt: 0, pb: { xs: 7, md: 12 }, bgcolor: "#F8FAFC" }}>
      <Container
        maxWidth={false}
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Heading */}
        <MotionBox
          textAlign="center"
          mb={6}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={headingVariants}
        >
          <Typography
            variant="overline"
            sx={{
              color: "#FF6B00",
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
            }}
          >
            WHY CHOOSE THE RIGHT TUTOR
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mt: 1,
              fontWeight: 800,
              color: "#0F172A",
              fontSize: { xs: "1.75rem", sm: "2.1rem", md: "2.5rem", lg: "3rem" },
              lineHeight: 1.2,
            }}
          >
            Why Learning with{" "}
            <Box
              component="span"
              sx={{
                position: "relative",
                display: "inline-block",
                px: 1,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "6px",
                  height: "38%",
                  bgcolor: "#FFF3E8",
                  borderRadius: 1,
                  zIndex: 0,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  position: "relative",
                  zIndex: 1,
                  color: "#FF6B00",
                }}
              >
                the Right Tutor
              </Box>
            </Box>{" "}
            Matters
          </Typography>

          <Typography
            sx={{
              mt: 3,
              color: "#64748B",
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.08rem" },
              mx: "auto",
              lineHeight: 1.9,
            }}
          >
            A great tutor does more than explain lessons—they inspire
            confidence, spark curiosity, strengthen understanding, and help
            students build lifelong learning habits. With the right guidance,
            every child gains the skills and confidence needed to succeed in
            school and beyond.
          </Typography>
        </MotionBox>

        {/* Cards */}
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 4, md: 8 } }}>
          {/* Learning Benefits */}
          <Grid size={{ xs: 12, md: 6 }}>
            <MotionPaper
              elevation={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={cardLeftVariants}
              whileHover={{
                y: -8,
                boxShadow: "0 18px 45px rgba(0,0,0,0.08)",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: 5,
                height: "100%",
                border: "1px solid #E2E8F0",
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "18px",
                  bgcolor: "#FFF3E8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <SchoolRoundedIcon
                  sx={{
                    color: "#FF6B00",
                    fontSize: { xs: 32, sm: 38 },
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                Learning Benefits
              </Typography>

              <Typography
                sx={{
                  mt: 2,
                  mb: 3,
                  color: "#64748B",
                  lineHeight: 1.8,
                }}
              >
                Personalized learning empowers students to understand concepts
                better, gain confidence, and achieve consistent academic growth
                through focused guidance.
              </Typography>

              <MotionList
                disablePadding
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={listContainerVariants}
              >
                {learningBenefits.map((item) => (
                  <MotionListItem
                    key={item}
                    disableGutters
                    variants={listItemVariants}
                    sx={{ py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 38 }}>
                      <CheckCircleRoundedIcon sx={{ color: "#FF6B00" }} />
                    </ListItemIcon>

                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        color: "#334155",
                      }}
                    />
                  </MotionListItem>
                ))}
              </MotionList>
            </MotionPaper>
          </Grid>

          {/* How It Works */}
          <Grid size={{ xs: 12, md: 6 }}>
            <MotionPaper
              elevation={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={cardRightVariants}
              whileHover={{
                y: -8,
                boxShadow: "0 18px 45px rgba(0,0,0,0.08)",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: 5,
                height: "100%",
                border: "1px solid #E2E8F0",
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "18px",
                  bgcolor: "#EEF4FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <ArrowForwardRoundedIcon
                  sx={{
                    color: "#1E3A8A",
                    fontSize: { xs: 32, sm: 38 },
                  }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                How It Works
              </Typography>

              <Typography
                sx={{
                  mt: 2,
                  mb: 3,
                  color: "#64748B",
                  lineHeight: 1.8,
                }}
              >
                Finding the right tutor is simple. Follow these easy steps to
                connect with expert educators and begin your child's journey
                toward academic excellence.
              </Typography>

              <MotionList
                disablePadding
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={listContainerVariants}
              >
                {howItWorks.map((item, index) => (
                  <MotionListItem
                    key={item}
                    disableGutters
                    variants={listItemVariants}
                    sx={{ py: 1.2 }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: "#1E3A8A",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>

                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        color: "#334155",
                      }}
                    />
                  </MotionListItem>
                ))}
              </MotionList>
            </MotionPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}